import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Instagram } from "lucide-react";
import Reveal from "./Reveal";
import { INSTAGRAM_HANDLE, INSTAGRAM_URL, videoPoems } from "../../data/content";

const FEATURED_IDS = ["treinta", "desprenderse"];
const featured = FEATURED_IDS.map((id) => videoPoems.find((v) => v.id === id)).filter(Boolean);
const rest = videoPoems.filter((v) => !FEATURED_IDS.includes(v.id));

const VideoCard = ({ vp, i }) => (
    <Reveal delay={Math.min(i, 5) * 0.1}>
        <div>
            <div
                data-testid={`video-poem-card-${vp.id}`}
                className="border border-paper/15 bg-black aspect-[9/16] max-h-[520px] w-full relative overflow-hidden hover:border-gold/60 transition-colors duration-500"
            >
                <video
                    data-testid={`video-poem-player-${vp.id}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    src={vp.src}
                    poster={vp.poster}
                    controls
                    playsInline
                    preload="none"
                />
            </div>
            <div className="mt-3 flex items-baseline justify-between gap-3">
                <p className="font-display text-xl text-paper">{vp.title}</p>
                <p className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    {vp.book}
                </p>
            </div>
        </div>
    </Reveal>
);

const VideoPoetry = () => {
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            const targetId = e.detail;
            const isRest = rest.some((v) => v.id === targetId);
            if (isRest) setExpanded(true);
            setTimeout(
                () => {
                    const el = document.querySelector(
                        `[data-testid="video-poem-player-${targetId}"]`
                    );
                    if (!el) return;
                    if (window.__lenis) {
                        window.__lenis.scrollTo(el, { offset: -110, duration: 1.2 });
                    } else {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                    el.play().catch(() => {});
                },
                isRest ? 800 : 150
            );
        };
        window.addEventListener("show-video", handler);
        return () => window.removeEventListener("show-video", handler);
    }, []);

    return (
        <section
            id="video-poesia"
            data-testid="video-poetry-section"
            className="relative py-24 sm:py-36 bg-ink text-paper overflow-hidden"
        >
            <img
                src="/media/trazo-bici.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -left-20 bottom-0 w-[440px] lg:w-[560px] opacity-[0.13] rotate-[3deg]"
            />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="mb-14 sm:mb-20 text-center">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">
                            04 — Vídeo poesía
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <img
                            data-testid="video-poetry-logo"
                            src="/media/logo-inandout.png"
                            alt="Poesía In And Out"
                            className="mx-auto mt-6 w-full max-w-[200px] sm:max-w-[280px]"
                        />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-paper/70">
                            Los versos salen del papel: voz, imagen y ritmo. Dos vídeo poemas
                            destacados para ver y escuchar aquí mismo — y toda la colección,
                            siempre creciendo, en Instagram.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
                    {featured.map((vp, i) =>
                        vp.id === "desprenderse" ? (
                            <div key={vp.id}>
                                <VideoCard vp={vp} i={i} />
                                <div className="mt-5 flex justify-center">
                                    <button
                                        data-testid="video-more-toggle"
                                        onClick={() => setExpanded(!expanded)}
                                        aria-expanded={expanded}
                                        className="flex items-center gap-3 border border-paper/25 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-paper/80 hover:text-gold hover:border-gold/60 transition-colors duration-300"
                                    >
                                        {expanded ? "Ver menos" : "Ver más"}
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <VideoCard key={vp.id} vp={vp} i={i} />
                        )
                    )}
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                data-testid="video-more-grid"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                                className="overflow-hidden order-3 lg:order-4 col-span-full"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
                                    {rest.map((vp, i) => (
                                        <VideoCard key={vp.id} vp={vp} i={i + featured.length} />
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <Reveal delay={0.3} className="order-4 lg:order-3 sm:col-span-2 lg:col-span-1">
                        <a
                            data-testid="video-poetry-more-instagram"
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group mt-2 flex w-full h-full min-h-[200px] flex-row flex-wrap items-center justify-center gap-4 border border-dashed border-gold/50 px-8 py-6 hover:bg-gold/10 transition-colors duration-500"
                        >
                            <Instagram size={22} className="text-gold" />
                            <span className="font-display italic text-xl sm:text-2xl text-paper/85">
                                Toda la colección vive en Instagram
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                                {INSTAGRAM_HANDLE}
                            </span>
                        </a>
                    </Reveal>
                </div>

                <Reveal delay={0.2}>
                    <div className="mt-16 sm:mt-20 flex flex-col items-center text-center border-t border-paper/15 pt-14">
                        <p className="font-display italic text-2xl sm:text-3xl text-paper/80 max-w-2xl leading-snug">
                            «La vida sin banda sonora es un espejismo que aún añora la
                            crudeza de tus silencios.»
                        </p>
                        <a
                            data-testid="instagram-external-link"
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-10 group flex items-center gap-4 bg-crimson text-paper px-8 sm:px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
                        >
                            <Instagram size={18} />
                            Sígueme en Instagram {INSTAGRAM_HANDLE}
                        </a>
                        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60">
                            Nuevos vídeo poemas cada semana
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default VideoPoetry;
